import { useForm, usePage } from "@inertiajs/react";
import {
  Children,
  cloneElement,
  type FormEvent,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
  useState,
} from "react";

import Button from "@/components/Button";
import LinkButton from "@/components/LinkButton";
import SearchableSelect from "@/components/SearchableSelect";
import { houses } from "@/routes";
import type { House, HouseStatus, SharedProps } from "@/types";

const STATUS_LABELS: Record<string, string> = {
  active: "Activa",
  inactive: "Inactiva",
};

const FIELD_INPUT_CLASSES =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink shadow-none placeholder:text-ink/40 focus:outline-none focus:ring-0 focus:shadow-none";

function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-4 rounded-2xl border-2 border-ink/20 p-5">
      <legend className="flex items-center gap-2 px-2 font-sans text-sm font-semibold text-ink">
        <span className="text-steel">{icon}</span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

/** Labels a single input/select the same way `Section` labels a card: the
 * name sits on the border as a legend once the field has a value, so a
 * filled field stays identifiable. While empty, only the placeholder (also
 * derived from `label`) shows, so the two don't display redundantly. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  const child = Children.only(children);
  const childProps = isValidElement(child)
    ? (child.props as { value?: unknown; required?: boolean })
    : undefined;
  const hasValue =
    childProps?.value !== undefined &&
    childProps?.value !== null &&
    childProps?.value !== "";
  const invalid = !!childProps?.required && !hasValue;
  const displayLabel = childProps?.required ? `${label} *` : label;
  const showLegend = hasValue || invalid;

  const content = isValidElement(child)
    ? cloneElement(child as ReactElement<{ placeholder?: string }>, {
        placeholder:
          (child.props as { placeholder?: string }).placeholder ?? displayLabel,
      })
    : child;

  return (
    <fieldset
      className={`min-w-0 rounded-full border-2 px-4 focus-within:border-steel ${showLegend ? "pb-2.5" : "py-3.5"} ${invalid ? "border-deep-red" : "border-ink"}`}
    >
      {showLegend && (
        <legend
          className={`truncate px-1.5 font-sans text-xs ${invalid ? "text-deep-red" : "text-ink/60"}`}
        >
          {displayLabel}
        </legend>
      )}
      {content}
    </fieldset>
  );
}

function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={FIELD_INPUT_CLASSES} {...props} />;
}

function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${FIELD_INPUT_CLASSES} resize-y`} {...props} />;
}

type Props = {
  data: House;
  communes: { id: number; name: string; region: string }[];
  house_managers: { id: number; name: string; rut: string }[];
};

export default function HousesEdit({
  data: house,
  communes,
  house_managers,
}: Props) {
  const { currentUser } = usePage<SharedProps>().props;
  const canViewPayment = currentUser?.origin !== "la_cuesta" || house.id === 58;
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const form = useForm({
    image: null as File | null,
    name: house.name ?? "",
    abbreviation: house.abbreviation ?? "",
    address: house.address ?? "",
    address_url: house.address_url ?? "",
    description: house.description ?? "",
    status: house.status ?? "",
    commune_id: house.commune_id ? String(house.commune_id) : "",
    user_id: house.user_id ? String(house.user_id) : "",
    payment_name: house.payment_name ?? "",
    payment_rut: house.payment_rut ?? "",
    payment_bank: house.payment_bank ?? "",
    payment_bank_account_type: house.payment_bank_account_type ?? "",
    payment_bank_account_number: house.payment_bank_account_number ?? "",
    payment_email: house.payment_email ?? "",
    payment_url: house.payment_url ?? "",
  });

  function selectImage(file: File | null) {
    form.setData("image", file);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  }

  const isNew = !house.id;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      e.currentTarget.reportValidity();
      return;
    }
    form.transform(({ image, ...data }) => ({
      house: {
        ...data,
        ...(image ? { image } : {}),
        commune_id: data.commune_id || null,
        user_id: data.user_id || null,
      },
    }));
    const options = { forceFormData: !!form.data.image };
    if (isNew) {
      form.post(houses.create().url, options);
    } else {
      form.patch(houses.update(house.id).url, options);
    }
  }

  return (
    <div className="mx-auto w-[90%] max-w-2xl py-10">
      <h1 className="mb-1 font-sans text-3xl font-semibold text-ink">
        {isNew ? "Nueva casa" : house.name || "Casa"}
      </h1>
      <p className="mb-6 font-sans text-sm text-ink/60">
        Los campos marcados con * son obligatorios.
      </p>

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Section icon={<HomeIcon />} title="Datos generales">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre">
              <Input
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                required
              />
            </Field>
            <Field label="Abreviación">
              <Input
                value={form.data.abbreviation}
                onChange={(e) =>
                  form.setData("abbreviation", e.target.value.toUpperCase())
                }
                minLength={2}
                required
              />
            </Field>
            <Field label="Estado">
              <SearchableSelect
                value={form.data.status}
                onChange={(v) => form.setData("status", v as HouseStatus)}
                options={Object.entries(STATUS_LABELS).map(
                  ([value, label]) => ({ value, label }),
                )}
              />
            </Field>
            <Field label="Dirección">
              <Input
                value={form.data.address}
                onChange={(e) => form.setData("address", e.target.value)}
              />
            </Field>
            <Field label="URL de dirección">
              <Input
                value={form.data.address_url}
                onChange={(e) => form.setData("address_url", e.target.value)}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Descripción">
                <Textarea
                  value={form.data.description}
                  onChange={(e) => form.setData("description", e.target.value)}
                  rows={3}
                />
              </Field>
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <span className="font-sans text-sm text-ink">
                Imagen de la casa
              </span>
              {(imagePreview ?? house.image_url) && (
                <img
                  src={imagePreview ?? (house.image_url as string)}
                  alt={house.name || "Casa"}
                  className="h-40 w-full rounded-2xl object-cover sm:w-80"
                />
              )}
              <label className="w-fit cursor-pointer rounded-full border-2 border-ink px-4 py-2 font-sans text-sm text-ink hover:border-steel">
                {imagePreview || house.image_url
                  ? "Cambiar imagen"
                  : "Subir imagen"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => selectImage(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          </div>
        </Section>

        <Section icon={<MapPinIcon />} title="Ubicación y encargado">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Comuna">
              <SearchableSelect
                value={form.data.commune_id}
                onChange={(v) => form.setData("commune_id", v)}
                options={[
                  { value: "", label: "Sin comuna" },
                  ...communes.map((c) => ({
                    value: String(c.id),
                    label: `${c.name} (${c.region})`,
                  })),
                ]}
                required
              />
            </Field>
            <Field label="Encargado de casa">
              <SearchableSelect
                value={form.data.user_id}
                onChange={(v) => form.setData("user_id", v)}
                options={[
                  { value: "", label: "Sin encargado" },
                  ...house_managers.map((u) => ({
                    value: String(u.id),
                    label: `${u.name} (${u.rut})`,
                  })),
                ]}
              />
            </Field>
          </div>
        </Section>

        {canViewPayment && (
          <Section icon={<CardIcon />} title="Datos de pago">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nombre">
                <Input
                  value={form.data.payment_name}
                  onChange={(e) => form.setData("payment_name", e.target.value)}
                />
              </Field>
              <Field label="RUT">
                <Input
                  value={form.data.payment_rut}
                  onChange={(e) => form.setData("payment_rut", e.target.value)}
                />
              </Field>
              <Field label="Banco">
                <Input
                  value={form.data.payment_bank}
                  onChange={(e) => form.setData("payment_bank", e.target.value)}
                />
              </Field>
              <Field label="Tipo de cuenta">
                <Input
                  value={form.data.payment_bank_account_type}
                  onChange={(e) =>
                    form.setData("payment_bank_account_type", e.target.value)
                  }
                />
              </Field>
              <Field label="Número de cuenta">
                <Input
                  value={form.data.payment_bank_account_number}
                  onChange={(e) =>
                    form.setData("payment_bank_account_number", e.target.value)
                  }
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={form.data.payment_email}
                  onChange={(e) =>
                    form.setData("payment_email", e.target.value)
                  }
                />
              </Field>
              <Field label="URL de pago">
                <Input
                  value={form.data.payment_url}
                  onChange={(e) => form.setData("payment_url", e.target.value)}
                />
              </Field>
            </div>
          </Section>
        )}

        <div className="mt-2 flex gap-3">
          <Button type="submit" disabled={form.processing}>
            Guardar
          </Button>
          <LinkButton href={houses.index().url}>Cancelar</LinkButton>
        </div>
      </form>
    </div>
  );
}

function HomeIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.5 11.5L12 4l8.5 7.5M6 10v9.5a1 1 0 001 1h3.5v-6h3v6H17a1 1 0 001-1V10"
      />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21s7-6.1 7-11.5A7 7 0 105 9.5C5 14.9 12 21 12 21z"
      />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path strokeLinecap="round" d="M2.5 9.5h19" />
      <path strokeLinecap="round" d="M6 15h4" />
    </svg>
  );
}
